import {
  showPRCategory,
  createPRCategory,
  editPRCategory,
  storePRCategory,
  updatePRCategory,
} from '@app/api/prCategories.api';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import StatusBar from '@app/components/statusBar/statusBar';
import TextArea from 'antd/lib/input/TextArea';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Form, notification, Tooltip, Button, Row, Col } from 'antd';
import { PRCategoryRequest } from '@app/models/pr_categories/PRCategoryRequest';
import { notificationController } from '@app/controllers/notificationController';

interface PRCategoryFormProps {
  editable?: boolean;
  id?: string;
  pageTitle?: string;
  type?: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

const PRCategoryForm: React.FC<PRCategoryFormProps> = ({
  editable = false,
  id,
  pageTitle,
  type,
  setCurrentBreadcrumName,
}) => {
  const [parentCategoryOptions, setPRCategoryOptions] = useState<{ label: string; value: string }[]>([]);
  const { t } = useTranslation();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | null>(null);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const [form] = Form.useForm();
  const hasFetchedRef = useRef(false);
  const [isLoading, setLoading] = useState(false);
  const pr_categoryId = id ? parseInt(id) : null;

  const setApiData = (res: any) => {
    form.setFieldsValue({
      pr_category: res?.pr_category?.id,
      code: res?.pr_category?.code,
      name: res?.pr_category?.name,
      description: res?.pr_category?.description,
      model: res.pr_category?.model,
      parent_id: res.pr_category?.parent_id,
      parent_name: res?.pr_category?.parent?.name,
      status: res?.pr_category?.status,
    });
    setStatus(res.status);
    setCurrentBreadcrumName(res?.pr_category?.code || 'New');
  };

  const setOptions = (res: any) => {
    const mappedStatuses = res.status_options.map((status: any) => ({
      label: status.label,
      value: status.value,
    }));
    setStatusOptions(mappedStatuses);
  };

  const setPRCOptions = (res: any) => {
    const mappedPRCOptions = res.root_options?.map((status: any) => ({
      label: status.label,
      value: status.value,
    }));
    setPRCategoryOptions(mappedPRCOptions);
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setLoading(true);
    if (pr_categoryId) {
      if (type === 'view') {
        showPRCategory(pr_categoryId)
          .then((res: any) => {
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to view PR Category', description: err.message });
            console.error(err);
          });
      } else if (type === 'edit') {
        editPRCategory(pr_categoryId)
          .then((res: any) => {
            setApiData(res);
            setOptions(res);
            setPRCOptions(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to edit PR Category', description: err.message });
            console.error(err);
          });
      }
    } else if (type === 'create') {
      createPRCategory()
        .then((res: any) => {
          setOptions(res);
          setPRCOptions(res);
          setLoading(false);
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: 'Failed to fetch PR Category', description: err.message });
          console.error(err);
        });
    }
  }, []);

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" loading={isLoading} onClick={() => form.submit()} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton
          onClick={() => navigate('/product-registrations-categories')}
          style={{ marginRight: '8px' }}
          size="small"
        >
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton
          type="primary"
          onClick={() => navigate(`/edit-product-registration-category`, { state: { id } })}
          size="small"
        >
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, isLoading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  const handleSubmit = (values: PRCategoryRequest) => {
    if (editable) {
      const submissionData = {
        ...values,
        ...values,
      };

      if (type === 'create') {
        storePRCategory(submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/view-product-registration-category`, { state: { id: res?.data?.id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: err.message });
            setLoading(false);
          });
      } else if (type === 'edit' && pr_categoryId) {
        updatePRCategory(pr_categoryId, submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/view-product-registration-category`, { state: { id: id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: `${err.message}` });
            setLoading(false);
          });
      }
    }
    console.log('Form Submitted');
  };

  return (
    <>
      <BaseRow gutter={[16, 16]} style={{ width: '100%' }}>
        <BaseCol span={24}>
          <StatusBar id={id} status={status} />
        </BaseCol>
        <BaseCol span={24}>
          <BaseCard id="PRCategoryForm" title={pageTitle} padding="1.25rem">
            <Form
              form={form}
              name="prCategoryForm"
              onFinish={handleSubmit}
              size="small"
              labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
              labelAlign="left"
              labelWrap
              wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
            >
              <Row gutter={16}>
                <Col xs={24} md={24} lg={12}>
                  <Form.Item
                    name="code"
                    label="Category Code"
                    rules={[{ required: true, message: 'Code is required' }]}
                  >
                    <BaseInput placeholder="Enter Code" readOnly={!editable} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} lg={12}>
                  <Form.Item
                    name="name"
                    label="Category Name"
                    rules={[{ required: true, message: 'Name is required' }]}
                  >
                    <BaseInput placeholder="Enter Category Name" readOnly={!editable} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={24} lg={12}>
                  <Form.Item label="Parent Category">
                    {!editable ? (
                      <BaseInput readOnly value={form.getFieldValue('parent_name') || ''} />
                    ) : (
                      <Form.Item name="parent_id" noStyle>
                        <BaseSelect
                          placeholder="Select Parent Category"
                          options={parentCategoryOptions}
                          disabled={!editable}
                        />
                      </Form.Item>
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} lg={12}>
                  <Form.Item name="model" label="Model">
                    <BaseInput placeholder="Enter Model Name" readOnly={!editable} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={24} lg={12}>
                  <Form.Item name="description" label="Description">
                    {!editable ? (
                      <BaseInput readOnly value={form.getFieldValue('description') || ''} />
                    ) : (
                      <BaseInput.TextArea placeholder="Enter Description" readOnly={!editable} />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} lg={12}>
                  <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Status is required' }]}>
                    {!editable ? (
                      <BaseInput readOnly />
                    ) : (
                      <BaseSelect placeholder="Select Status" options={statusOptions} disabled={!editable} />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </BaseCard>
        </BaseCol>
      </BaseRow>
    </>
  );
};

export default PRCategoryForm;
